import classNames from 'classnames';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { DatePicker } from 'components/shared/DatePicker/DatePicker';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { InfoTooltip } from 'components/shared/InfoTooltip';
import { useCopyTextToClipboard } from 'hooks/useCopyToClipboard';
import type { DateListFieldData, DateListFieldMetadata } from 'models/forms/fields';
import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

const CopyAllButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
`;

const StyledToolTip = styled(InfoTooltip)`
  position: absolute;
  left: 0;
`;

export const CustomizedReminder: FC<{ id: string; className?: string }> = ({ id, className }) => (
  <div className="d-inline position-relative">
    <StyledToolTip
      id={id + '_customized'}
      className={classNames('text-info', className)}
      tooltipKey="form.StrikeDates.customized"
    />
  </div>
);

export const LabelContent: FC<{ isCustomized?: boolean; id: string; onCopyToClipboard: () => void }> = ({
  isCustomized,
  id,
  onCopyToClipboard,
}) => {
  const { formatMessage } = useIntl();
  return (
    <>
      {isCustomized && <CustomizedReminder id={id} />}
      <CopyAllButton className="btn btn-flat-secondary btn-icon-end py-1 mt-n1" onClick={onCopyToClipboard}>
        {formatMessage({ id: 'form.buttons.CopyAll' })}
        <i className="icon ps-1">file_copy</i>
      </CopyAllButton>
    </>
  );
};

export const DatesListField: FC<ProductNodeBuilderProps> = ({ node: { id }, tabIndex }) => {
  const [data, setData] = useData<DateListFieldData>(id);
  const metadata = useFieldMetadata<DateListFieldMetadata>(id)!;
  const translationKey = useFieldTranslationKey(id);
  const isFieldInvalid = useIsFieldInvalid(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const { allowWeekend, maxSelectedValues, startDate, endDate } = useFieldMetadata<DateListFieldMetadata>(id)!;
  const hasTooManySelectedValues =
    maxSelectedValues !== undefined && maxSelectedValues > 0 && (data?.length ?? 0) > maxSelectedValues;
  const { isReadOnly, isCustomized } = metadata;

  const generateValues = useCallback(() => data?.join(',') ?? '', [data]);

  const onCopyToClipboard = useCopyTextToClipboard();

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as any}
      labelContent={
        <LabelContent
          id={id}
          isCustomized={isCustomized}
          onCopyToClipboard={() => onCopyToClipboard(generateValues())}
        />
      }
      className={classNames('position-relative', patchedFieldClassName)}
    >
      <DatePicker
        type="multi"
        values={data ?? undefined}
        id={`datelist-${id}`}
        onChange={setData}
        allowWeekend={allowWeekend}
        disabled={isReadOnly}
        hasError={isFieldInvalid || hasTooManySelectedValues}
        minDate={startDate}
        maxDate={endDate}
        tabIndex={tabIndex}
      />
    </BlockOrientationFormGroup>
  );
};

DatesListField.displayName = 'DateListField';
